import { useEffect, useState, type ReactNode } from "react";
import { Link } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import {
  Cookie,
  Settings,
  Shield,
  BarChart3,
  Megaphone,
  SlidersHorizontal,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useCookieConsent,
  type CookiePreferences,
} from "./cookie-consent-provider";

const bannerVariants = {
  hidden: { y: 100, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 300, damping: 30 },
  },
  exit: {
    y: 100,
    opacity: 0,
    transition: { duration: 0.2 },
  },
};

export function CookieBanner() {
  const {
    showBanner,
    acceptAll,
    acceptNecessary,
    savePreferences,
    preferences,
    setShowBanner,
  } = useCookieConsent();
  const [showSettings, setShowSettings] = useState(false);
  const [tempPreferences, setTempPreferences] =
    useState<CookiePreferences>(preferences);

  // Keep the dialog draft in sync if the stored preferences change while it
  // is open (e.g. after a Withdraw consent action elsewhere).
  useEffect(() => {
    setTempPreferences(preferences);
  }, [preferences]);

  const handleOpenSettings = () => {
    setTempPreferences(preferences);
    setShowSettings(true);
  };

  const handleSaveSettings = () => {
    savePreferences(tempPreferences);
    setShowSettings(false);
  };

  const handleRejectInDialog = () => {
    acceptNecessary();
    setShowSettings(false);
  };

  const handleAcceptAllInDialog = () => {
    acceptAll();
    setShowSettings(false);
  };

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <motion.div
            variants={bannerVariants}
            initial={false}
            animate="visible"
            exit="exit"
            className="relative z-40 w-full bg-background px-3 py-2 max-h-[45dvh] overflow-y-auto"
            data-testid="cookie-banner"
            role="dialog"
            aria-modal="false"
            aria-labelledby="cookie-banner-title"
            aria-describedby="cookie-banner-desc"
          >
              <div className="site-card mx-auto max-w-5xl shadow-sm overflow-hidden">
              <div className="px-4 py-4 md:px-5 md:py-5 flex flex-col gap-4">
                <div className="flex items-start gap-3">
                  <div className="rounded-full bg-primary/5 p-2 border border-primary/10 flex-shrink-0 hidden sm:block">
                    <Cookie className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      id="cookie-banner-title"
                      className="text-sm font-semibold text-foreground mb-1"
                    >
                      Rispettiamo la tua privacy
                    </p>
                    <p
                      id="cookie-banner-desc"
                       className="text-sm text-muted-foreground leading-relaxed"
                    >
                      Usiamo cookie tecnici, e — solo con il tuo consenso —
                      cookie di analisi e marketing per migliorare il sito e
                      misurarne le prestazioni. Puoi accettare tutto, rifiutare
                      tutto o scegliere nel dettaglio. Cambierai idea quando
                      vuoi dal link "Preferenze cookie" nel footer. Maggiori
                      informazioni nella{" "}
                      <Link
                        href="/cookie-policy"
                        className="underline text-primary-strong hover:text-primary-strong/80 transition-colors"
                        data-testid="link-cookie-policy-banner"
                      >
                        Cookie Policy
                      </Link>
                      .
                    </p>
                  </div>
                </div>
                {/* GDPR: Reject and Accept must have equal prominence — same
                    size and same visual weight. We render them as two solid
                    primary buttons; "Personalizza" is a secondary action. */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Button
                    variant="outline"
                    onClick={handleOpenSettings}
                     className="font-medium"
                    data-testid="button-cookie-customize"
                  >
                    <SlidersHorizontal className="h-4 w-4 mr-2" />
                    Personalizza
                  </Button>
                  <Button
                    onClick={acceptNecessary}
                     className="font-medium bg-secondary text-secondary-foreground hover:bg-secondary"
                    data-testid="button-cookie-necessary"
                  >
                    Rifiuta tutto
                  </Button>
                  <Button
                    onClick={acceptAll}
                     className="font-medium"
                    data-testid="button-cookie-accept-all"
                  >
                    Accetta tutto
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-2xl p-0 max-h-[90dvh] overflow-y-auto border-border bg-card">
          <DialogHeader className="px-6 pt-6 pb-5 sm:px-8 sm:pt-8 sm:pb-6 border-b border-border bg-muted/30">
            <DialogTitle className="flex items-center gap-3 font-extrabold text-2xl">
              <Settings className="h-6 w-6 text-primary" />
              Preferenze cookie
            </DialogTitle>
            <DialogDescription className="text-sm sm:text-base mt-2">
              Scegli per ogni categoria. I cookie strettamente necessari sono
              sempre attivi perché indispensabili al funzionamento del sito.
              Puoi modificare o revocare il consenso in qualsiasi momento dal
              footer.
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 py-5 sm:px-8 sm:py-6 space-y-4 max-h-[60vh] overflow-y-auto">
            <CategoryRow
              icon={<Shield className="h-5 w-5 text-primary/60" />}
              id="necessary"
              title="Strettamente necessari"
              description="Indispensabili per il corretto funzionamento del sito (es. sessione, sicurezza, preferenza di tema). Non possono essere disattivati."
              checked
              disabled
            />

            <CategoryRow
              icon={<SlidersHorizontal className="h-5 w-5 text-primary" />}
              id="preferences"
              title="Preferenze e personalizzazione"
              description="Memorizzano le tue scelte (es. lingua) per offrirti un'esperienza personalizzata. Mappati su functionality_storage e personalization_storage di Google Consent Mode V2."
              checked={tempPreferences.preferences}
              onChange={(checked) =>
                setTempPreferences((prev) => ({ ...prev, preferences: checked }))
              }
              testId="switch-cookie-preferences"
            />

            <CategoryRow
              icon={<BarChart3 className="h-5 w-5 text-primary" />}
              id="analytics"
              title="Statistici / analytics"
              description="Ci aiutano a contare le visite e capire come è usato il sito (es. Google Analytics 4). I dati sono trattati in forma aggregata e con IP anonimizzato."
              checked={tempPreferences.analytics}
              onChange={(checked) =>
                setTempPreferences((prev) => ({ ...prev, analytics: checked }))
              }
              testId="switch-cookie-analytics"
            />

            <CategoryRow
              icon={<Megaphone className="h-5 w-5 text-primary" />}
              id="marketing"
              title="Marketing e profilazione"
              description="Usati per mostrare annunci pertinenti e misurarne le performance (es. Google Ads, Meta Pixel). Mappati su ad_storage, ad_user_data e ad_personalization di Consent Mode V2."
              checked={tempPreferences.marketing}
              onChange={(checked) =>
                setTempPreferences((prev) => ({ ...prev, marketing: checked }))
              }
              testId="switch-cookie-marketing"
            />
          </div>

          <DialogFooter className="px-6 py-5 sm:px-8 sm:py-6 border-t border-border bg-muted/30 flex-col sm:flex-row gap-2 sm:gap-3 sm:justify-between">
            <Button
              variant="outline"
              onClick={handleRejectInDialog}
              className="w-full sm:w-auto font-medium"
              data-testid="button-cookie-reject-all-dialog"
            >
              Rifiuta tutto
            </Button>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:justify-end">
              <Button
                variant="outline"
                onClick={handleSaveSettings}
                className="w-full sm:w-auto font-medium"
                data-testid="button-cookie-save"
              >
                Salva le mie scelte
              </Button>
              <Button
                onClick={handleAcceptAllInDialog}
                className="w-full sm:w-auto font-medium"
                data-testid="button-cookie-accept-all-dialog"
              >
                Accetta tutto
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface CategoryRowProps {
  icon: ReactNode;
  id: string;
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
  testId?: string;
}

function CategoryRow({
  icon,
  id,
  title,
  description,
  checked,
  disabled,
  onChange,
  testId,
}: CategoryRowProps) {
  return (
     <div className="site-card flex items-start gap-4 p-4 sm:p-5">
      <div className="mt-0.5 flex-shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3 mb-1.5">
          <Label
            htmlFor={id}
            className="font-medium text-sm sm:text-base cursor-pointer"
          >
            {title}
          </Label>
          <Switch
            id={id}
            checked={checked}
            disabled={disabled}
            onCheckedChange={onChange}
            className={disabled ? "opacity-50" : undefined}
            data-testid={testId ?? `switch-cookie-${id}`}
          />
        </div>
         <p className="text-sm text-muted-foreground leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  );
}
